import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import * as RT from "@radix-ui/react-tabs";
import * as DM from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { cx } from "../lib/cx";

export interface TabDef {
  value: string;
  label: ReactNode;
  count?: number | null;
  content: ReactNode;
}

export function Tabs({ tabs, value, onValueChange, defaultValue, label }: {
  tabs: TabDef[];
  value?: string;
  onValueChange?: (v: string) => void;
  defaultValue?: string;
  label: string;
}) {
  // Valor atual também fora do Radix (para o indicador saber para onde ir no modo não controlado).
  const [inner, setInner] = useState(defaultValue ?? tabs[0]?.value);
  const current = value ?? inner;
  const listRef = useRef<HTMLDivElement>(null);
  const [ind, setInd] = useState<{ x: number; w: number } | null>(null);
  const [ready, setReady] = useState(false);

  // Indicador deslizante: mede a aba ativa (e de novo quando a lista muda de tamanho); a aba ativa fica à vista.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const el = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
      if (!el || el.offsetWidth === 0) return setInd(null);
      const pad = 8;
      setInd({ x: el.offsetLeft + pad, w: Math.max(0, el.offsetWidth - pad * 2) });
      // Só rolagem horizontal da própria lista (scrollIntoView rolaria a página até a lista).
      if (el.offsetLeft < list.scrollLeft) list.scrollLeft = el.offsetLeft - 16;
      else if (el.offsetLeft + el.offsetWidth > list.scrollLeft + list.clientWidth)
        list.scrollLeft = el.offsetLeft + el.offsetWidth - list.clientWidth + 16;
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(list);
    return () => ro?.disconnect();
  }, [current, tabs.length]);
  useEffect(() => {
    if (!ind || ready) return;
    const t = window.setTimeout(() => setReady(true), 50);
    return () => window.clearTimeout(t);
  }, [ind, ready]);

  return (
    <RT.Root
      value={value}
      onValueChange={(v) => {
        setInner(v);
        onValueChange?.(v);
      }}
      defaultValue={defaultValue ?? tabs[0]?.value}
    >
      <RT.List
        ref={listRef}
        className={cx("vx-tabs__list", ind && "vx-tabs__list--ind", ready && "vx-tabs__list--ready")}
        aria-label={label}
      >
        {tabs.map((t) => (
          <RT.Trigger key={t.value} value={t.value} className="vx-tabs__trigger">
            {t.label}
            {t.count !== undefined && t.count !== null ? <span className="vx-tabs__count">{t.count}</span> : null}
          </RT.Trigger>
        ))}
        {ind ? (
          <span
            className="vx-tabs__ind"
            aria-hidden="true"
            style={{ "--vx-tab-x": `${ind.x}px`, "--vx-tab-w": `${ind.w}px` } as CSSProperties}
          />
        ) : null}
      </RT.List>
      {tabs.map((t) => (
        <RT.Content key={t.value} value={t.value}>
          {t.content}
        </RT.Content>
      ))}
    </RT.Root>
  );
}

// ---------------------------------------------------------------- menu

export interface MenuItemDef {
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  checked?: boolean;
  separatorBefore?: boolean;
}

export function Menu({ trigger, items, label, align = "end" }: {
  trigger: ReactNode;
  items: MenuItemDef[];
  label?: ReactNode;
  align?: "start" | "end";
}) {
  return (
    <DM.Root>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content className="vx-menu" align={align} sideOffset={4}>
          {label ? <DM.Label className="vx-menu__label">{label}</DM.Label> : null}
          {items.map((it, i) => (
            <span key={i} style={{ display: "contents" }}>
              {it.separatorBefore ? <DM.Separator className="vx-menu__sep" /> : null}
              <DM.Item
                className={cx("vx-menu__item", it.danger && "vx-menu__item--danger")}
                disabled={it.disabled}
                onSelect={it.onSelect}
              >
                {it.icon}
                <span>{it.label}</span>
                {it.checked ? (<><Check className="vx-menu__check" aria-hidden="true" /><span className="sr-only">(atual)</span></>) : null}
              </DM.Item>
            </span>
          ))}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
